/**
 * CPI-1363 — the Vietnamese name rule, shared by every CRM screen that edits a customer name.
 *
 * Vietnam only: callers gate on `country.country_code === 'vn'` (the same gate as the VN mobile
 * regex), so Malaysian names — "Muthu A/L Rajan", "Ahmad @ Ali" — never meet this rule.
 *
 * Two rules, chosen by identity type, because the Name field doubles as the company name when the
 * identity type is Enterprise Registration Certificate ('3') and company names legitimately carry
 * digits ("Công ty TNHH 3A"):
 *   - individual: letters, spaces and . ' -   (no digits)
 *   - company:    the same plus digits and & , ( ) /
 * Both accept the curly apostrophe ’ (phones auto-insert it) and require at least one letter.
 *
 * Validation normalises to NFC first and uses Unicode letter classes, never a hand-written list or
 * a range like À-ỹ: Vietnamese text arrives in two keyboard forms ("ễ" as one precomposed
 * character, or "e" + combining marks), and a listed alphabet rejects the second form while letting
 * symbols such as × and ÷ through. `\p{M}` keeps the combining marks of the decomposed form legal
 * during typing; what is SAVED is the NFC form (normalizeName), so duplicate search (CPI-1327)
 * matches the same name typed on different devices.
 */

// Identity-type value of Enterprise Registration Certificate in the customer forms.
export const IDENTITY_TYPE_ENTERPRISE = '3';

export const NAME_MAX_LENGTH = 100;

const INDIVIDUAL_NAME_REGEX = /^[\p{L}\p{M}\s.'’-]+$/u;
const COMPANY_NAME_REGEX = /^[\p{L}\p{M}\p{N}\s.'’\-&,()/]+$/u;
const HAS_LETTER_REGEX = /\p{L}/u;

/** NFC-normalise, trim, and collapse repeated whitespace — the form this rule saves. */
export function normalizeName(value) {
  if (typeof value !== 'string') return '';
  return value.normalize('NFC').trim().replace(/\s+/g, ' ');
}

/**
 * Whether a (non-empty) name satisfies the Vietnamese rule. Empty input answers true — required
 * is its own rule, and preferred name is optional.
 */
export function isValidVietnamName(value, { isCompany = false } = {}) {
  const normalized = normalizeName(value);
  if (!normalized) return true;
  if (normalized.length > NAME_MAX_LENGTH) return false;
  if (!HAS_LETTER_REGEX.test(normalized)) return false;
  return (isCompany ? COMPANY_NAME_REGEX : INDIVIDUAL_NAME_REGEX).test(normalized);
}
